import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import {
  Panel,
  Stat,
  Tag,
  Button,
  Icon,
  EmptyState,
  Field,
  Input,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import { formatPesos, formatPesosCompact, formatCount } from '@dayon/money';
import type { DateRange } from '@dayon/contracts';
import { GAMING_DAY_TIMEZONE, gamingDate, gamingDayOf } from '@dayon/contracts';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { downloadRun } from '../reports/download.ts';
import { ComparisonChart, WeeklyBars } from './charts.tsx';

const RANGES: { key: DateRange; label: string }[] = [
  { key: 'TODAY', label: 'Today' },
  { key: 'SEVEN_DAYS', label: '7D' },
  { key: 'THIRTY_DAYS', label: '30D' },
  { key: 'QTD', label: 'QTD' },
  /* The fifth range the contract has accepted all along. `dashboardQuery`
     defines CUSTOM with `from`/`to` and `resolveWindow` has a branch for it,
     but no screen ever sent one — so the only way to ask for an arbitrary
     window was to type the URL by hand. */
  { key: 'CUSTOM', label: 'Custom' },
];

/* ═══ THE CUSTOM WINDOW ═════════════════════════════════════════════════════════════════
   Shared by BOTH dashboards, in one copy.

   It lives in this module rather than in Dashboard.tsx because Dashboard.tsx
   already imports this one; the other direction would be an import cycle. What
   matters is that there is exactly ONE copy of the boundary rule below —
   `packages/db/src/gaming-day.ts` exists one layer down for precisely that
   reason, after two writers with two copies of the same day boundary wrote
   `reporting.daily_metric` twice for every gaming day.

   THE CONVENTION, since something has to decide it. An `<input type="date">`
   yields a bare calendar date and the API wants a full ISO instant
   (`z.iso.datetime()`), so "1 Sep" has to be turned into a moment. It is read
   as the GAMING DAY of 1 September — 00:00 in Asia/Manila, which is 16:00Z on
   31 August — because everything a custom window can draw is read from
   `reporting.daily_metric`, which is keyed on the gaming day. A window cut on
   UTC midnight instead would drop the first eight hours of the first day and
   pick up eight hours of the day after the last, and the trend chart would
   disagree with the KPI band beside it — exactly the bug the TODAY range had
   before `resolveWindow` started calling `gamingDayStart()`.

   BOTH CHOSEN DAYS ARE INCLUDED. The window is half-open `[from, to)`, so `to`
   is the Manila midnight that CLOSES the last chosen day: "1 Sep to 2 Sep"
   sends 2026-08-31T16:00:00.000Z → 2026-09-02T16:00:00.000Z and covers two whole
   gaming days. A single day is asked for by picking it in both fields.

   Asia/Manila is UTC+8 and has observed no daylight saving since 1978, so the
   offset is written here as a constant and the assumption is stated rather than
   hidden. The server draws the same boundary through `Intl` in
   `packages/db/src/gaming-day.ts`, which is where a future DST-observing
   licence would have to be handled.
   ═════════════════════════════════════════════════════════════════════════════════════ */

const GAMING_DAY_OFFSET = '+08:00';

const manilaDay = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: GAMING_DAY_TIMEZONE,
});

/** The gaming day an instant falls in, named: "01 Sep 2026". */
export const gamingDayName = (iso: string): string => manilaDay.format(new Date(iso));

/**
 * The last day a half-open window INCLUDES.
 *
 * `summary.range.to` is the boundary that closes the window, so it is already
 * the next gaming day. Naming it directly would print a day the figures do not
 * contain, which is how a report gets quoted with a date it never covered.
 */
export const lastIncludedDayName = (iso: string): string =>
  manilaDay.format(new Date(Date.parse(iso) - 1));

/** Today's gaming day, as the `YYYY-MM-DD` a date input speaks. */
export const gamingDayToday = (): string => gamingDayOf(new Date());

export interface CustomWindow {
  /** The instants to send, or null while the pair cannot be trusted. */
  from: string | null;
  to: string | null;
  /** Per-field, rendered INLINE. Nothing has been requested, so nothing failed. */
  errors: { from?: string; to?: string };
}

const DATE_SHAPE = /^\d{4}-\d{2}-\d{2}$/;

/** Manila midnight opening the gaming day `ymd`, as an instant. */
function gamingDayOpen(ymd: string): Date | null {
  if (!DATE_SHAPE.test(ymd)) return null;
  const at = new Date(`${ymd}T00:00:00.000${GAMING_DAY_OFFSET}`);
  if (Number.isNaN(at.getTime())) return null;
  /* A half-typed year — "0202" on the way to "2026" — parses perfectly well and
     would fetch a window nobody asked for on every keystroke. The platform has
     no data outside this range, so a date outside it is a typo, not a query. */
  const year = Number(ymd.slice(0, 4));
  return year < 2000 || year > 2100 ? null : at;
}

/**
 * Read the two date inputs as a window, or say why they cannot be read.
 *
 * Returns `from`/`to` as nulls whenever ANY field is wrong, which is what the
 * screens hang "do not fetch" on: an incomplete or reversed pair must not send
 * a request at all, because the previous range's figures would then sit under a
 * Custom button as though they answered it.
 */
export function customWindow(from: string, to: string): CustomWindow {
  const opensFrom = from ? gamingDayOpen(from) : null;
  const opensTo = to ? gamingDayOpen(to) : null;
  const errors: { from?: string; to?: string } = {};

  /*
   * A field that is merely EMPTY is only called out once the other one has been
   * filled. Firing two alerts the instant somebody opens the control — before
   * they have done anything, and with the panel below already explaining that
   * nothing is requested until both dates are set — is how a screen teaches
   * people to ignore its alerts.
   */
  if (!from) {
    if (to) errors.from = 'Pick the first day of the window.';
  } else if (!opensFrom) {
    errors.from = 'Use a real date between 2000 and 2100.';
  }
  if (!to) {
    if (from) errors.to = 'Pick the last day of the window.';
  } else if (!opensTo) {
    errors.to = 'Use a real date between 2000 and 2100.';
  }

  if (opensFrom && opensTo && opensFrom.getTime() > opensTo.getTime()) {
    errors.to = 'The last day is before the first, so there is no window to read.';
  }

  if (!opensFrom || !opensTo || errors.from || errors.to) return { from: null, to: null, errors };
  return {
    from: opensFrom.toISOString(),
    /* The upper bound CLOSES the last chosen gaming day, so both ends are
       included. Adding 24h of milliseconds is exact in a zone with no DST. */
    to: new Date(opensTo.getTime() + 86_400_000).toISOString(),
    errors,
  };
}

/**
 * The two date inputs, revealed when Custom is chosen.
 *
 * Field validation is INLINE, through the same `Field`/`error` idiom the access
 * module's export dialog uses. A modal is for a request that failed; a date that
 * has not been picked yet has not failed at anything.
 */
export function CustomRangeFields({
  from,
  to,
  onFrom,
  onTo,
  errors,
  window,
}: {
  from: string;
  to: string;
  onFrom: (value: string) => void;
  onTo: (value: string) => void;
  errors: { from?: string; to?: string };
  /** The instants that will be sent, once both dates are readable. */
  window: { from: string | null; to: string | null };
}) {
  return (
    <div className="dashboard-custom" data-testid="custom-range">
      <div className="dashboard-custom-fields">
        <Field label="From" error={errors.from}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="date"
              value={from}
              min="2000-01-01"
              max={gamingDayToday()}
              onChange={(e) => onFrom(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
            />
          )}
        </Field>
        <Field label="To" error={errors.to}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="date"
              value={to}
              min="2000-01-01"
              max={gamingDayToday()}
              onChange={(e) => onTo(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
            />
          )}
        </Field>
      </div>
      <div className="notice notice-quiet" data-testid="custom-range-note">
        <Icon name="calendar-blank" />
        <div>
          {window.from && window.to ? (
            <>
              Both days are included. The window is cut on <strong>Asia/Manila gaming days</strong>{' '}
              — <span className="tabular">{window.from}</span> to{' '}
              <span className="tabular">{window.to}</span> — so it lines up with the daily totals
              these figures are read from.
            </>
          ) : (
            <>
              Both days are included, and the window is cut on{' '}
              <strong>Asia/Manila gaming days</strong> so that it lines up with the daily totals the
              figures are read from. Nothing is requested until both dates are set.
            </>
          )}
        </div>
      </div>
    </div>
  );
}

interface TrendPoint {
  day: string;
  ggr: string;
  deposits: string;
  withdrawals: string;
  activePlayers: number;
}

interface BiPayload {
  summary: {
    range: { from: string; to: string; comparedFrom: string; comparedTo: string };
    ggr: { value: string; previous: string; deltaBp: number | null };
    deposits: { value: string; previous: string; deltaBp: number | null };
    withdrawals: { value: string; previous: string; deltaBp: number | null };
    activePlayers: { value: number; previous: number; deltaBp: number | null };
    holdBp: number | null;
    arpu: string;
  };
  trend: TrendPoint[];
  priorTrend: TrendPoint[];
  weeklyFlows: { week: string; deposits: string; withdrawals: string }[];
  topGames: {
    gameId: string;
    name: string;
    provider: string;
    ggr: string;
    holdBp: number | null;
    rounds: number;
  }[];
  topPlayers: {
    playerId: string;
    ref: string;
    name: string;
    brandName: string;
    agentRef: string | null;
    tier: string;
    deposits: string;
    withdrawals: string;
    netDeposits: string;
    ggr: string;
  }[];
}

function Delta({ bp }: { bp: number | null }) {
  if (bp === null) return <span className="text-subtle">no prior period</span>;
  if (bp === 0) return <span>flat</span>;
  const up = bp > 0;
  return (
    <span className={up ? 'stat-delta up' : 'stat-delta'}>
      {up ? '▲' : '▼'} {Math.abs(bp / 100).toFixed(1)}% vs prior
    </span>
  );
}

/**
 * Dashboard B — Period BI.
 *
 * The reporting view: five KPIs including ARPU, the GGR trend drawn against the
 * immediately prior period of equal length, weekly deposit/withdrawal bars, and
 * the wide top-players table with its full column set.
 */
export function PeriodBI() {
  const { brandId, allowed } = useSession();
  const { toast } = useToast();
  const [exporting, setExporting] = useState(false);
  const canExport = allowed('audit.export');
  const { showError } = useErrorDialog();
  const navigate = useNavigate();

  const [range, setRange] = useState<DateRange>('THIRTY_DAYS');
  /* The two date inputs, as typed. They are kept when the operator switches
     away to 30D and back, so returning to Custom does not lose the window. */
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [data, setData] = useState<BiPayload | null>(null);
  /*
   * WHICH RANGE THE FIGURES ON SCREEN WERE FETCHED FOR.
   *
   * Not the range the control is currently set to: between the click and the
   * response those differ, and the caption has to name the window the numbers
   * actually came from rather than the one that has just been asked for.
   */
  const [shownRange, setShownRange] = useState<DateRange>('THIRTY_DAYS');
  const [loading, setLoading] = useState(true);
  /*
   * Whether the last read FAILED, as distinct from having returned nothing.
   *
   * This is a reporting view, so every empty branch on it is a finding: "no
   * activity in this range · a real zero, not a missing feed" is precisely the
   * sentence an analyst quotes. It is only true of a read that came back. A
   * failed one knows nothing about the period, so the figures are dropped and
   * this flag says so where they were — never a zero, never a silent blank.
   */
  const [loadFailed, setLoadFailed] = useState(false);
  /* Bumped by Try again: after a failure the range control is off screen, so
     this is the only way back short of reloading the page. */
  const [attempt, setAttempt] = useState(0);

  /*
   * The custom window, read off the two date inputs.
   *
   * `requestedFrom` / `requestedTo` are null unless CUSTOM is selected AND both
   * dates are readable and the right way round — which is what stops an
   * incomplete pair from firing a request, and what keeps the effect from
   * re-running when dates are typed under a range that ignores them.
   */
  const custom = customWindow(customFrom, customTo);
  const requestedFrom = range === 'CUSTOM' ? custom.from : null;
  const requestedTo = range === 'CUSTOM' ? custom.to : null;
  const awaitingCustom = range === 'CUSTOM' && (requestedFrom === null || requestedTo === null);

  useEffect(() => {
    /*
     * An unusable custom pair asks for NOTHING.
     *
     * The figures for the previous range go with it. Leaving them up would put
     * a 30-day GGR under a Custom button with two half-filled dates beside it,
     * and the caption would be the only thing on screen saying otherwise.
     */
    if (range === 'CUSTOM' && (requestedFrom === null || requestedTo === null)) {
      setData(null);
      setLoadFailed(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    const query = new URLSearchParams({ range });
    if (requestedFrom && requestedTo) {
      query.set('from', requestedFrom);
      query.set('to', requestedTo);
    }
    if (brandId) query.set('brandId', brandId);

    api
      .get<BiPayload>(`/api/dashboard/bi?${query}`)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setShownRange(range);
        setLoadFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // The prior window's figures go with the failure: a comparison drawn
        // for one period under another period's dates is worse than no chart.
        setData(null);
        setLoadFailed(true);
        showError({
          title: 'Could not load performance',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [range, requestedFrom, requestedTo, brandId, showError, attempt]);

  /**
   * Export what is on screen.
   *
   * Runs an ad-hoc report over `daily_metric` for the window the BI view is
   * showing — the same rollup, the same dates, the same brand scope — so the
   * file and the screen cannot disagree. It goes through the reports module
   * rather than a private export path, which is what makes it turn up in Run
   * history with the operator's name against it.
   */
  const exportPeriod = useCallback(() => {
    // Read off `data`, not the destructured `summary` below it: that binding
    // exists only after the loading and failure branches have returned, and
    // this callback is created before them.
    const window = data?.summary.range;
    if (!window) return;
    setExporting(true);
    api
      .post<{ ref: string; rowCount: number }>('/api/reports/run', {
        source: 'daily_metric',
        dimensions: ['day', 'brand'],
        measures: ['deposits', 'withdrawals', 'ggr', 'bonus_cost', 'active_players'],
        /* NAMED ON THE GAMING DAY, not on UTC.
           `slice(0, 10)` takes the UTC date out of an ISO instant, and these
           bounds mean Manila days. Between 16:00 and 24:00 UTC the two differ,
           so the export asked for — and the file was named after — the gaming
           day BEFORE the one the operator was looking at. The figures were
           real; they were the wrong day's, and the caption above them said so
           in Manila while the file said otherwise. */
        from: gamingDayOf(window.from),
        to: gamingDayOf(window.to),
        ...(brandId ? { brandId } : {}),
      })
      .then((run) => downloadRun(run.ref, showError, toast))
      .catch((error: unknown) => {
        showError({
          title: 'Could not export this period',
          message: error instanceof ApiRequestError ? error.message : 'Nothing was exported.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      })
      .finally(() => setExporting(false));
  }, [data, brandId, showError, toast]);

  /**
   * The heading, the range control and — when Custom is chosen — the two date
   * inputs.
   *
   * Lifted out of the main return because the screen has three states that all
   * need it: the figures, the failed read, and a custom window that has not
   * been finished yet. A range control that disappears in two of them leaves
   * the operator with no way back to 30D short of reloading the page.
   */
  const header = (caption: ReactNode) => (
    <>
      <div className="row">
        <div>
          <h3 style={{ margin: 0 }}>Performance</h3>
          <div className="text-subtle" style={{ fontSize: '12.5px' }}>
            {caption}
          </div>
        </div>
        <div className="seg header-spacer dashboard-range">
          {RANGES.map((r) => (
            <label className="seg-opt" key={r.key}>
              <input
                type="radio"
                name="bi-range"
                checked={range === r.key}
                onChange={() => setRange(r.key)}
              />
              {r.label}
            </label>
          ))}
        </div>
        {/*
          This button was disabled for as long as nothing stored a report run.
          `reporting.report_run` and `report_artefact` now exist, so it works:
          it runs the SAME window that is on screen, over the same
          `reporting.daily_metric` rollup these figures are read from, through
          the reports module — which means the export is recorded, attributable
          and downloadable again later rather than being a one-off file.

          Gated on audit.export rather than on the dashboard's own capability:
          this produces a file that leaves the platform, and a role that may
          read the dashboard is not automatically a role that may export it.
          Also disabled when no window is on screen: there would be nothing to
          take a copy of.
        */}
        <Button
          onClick={exportPeriod}
          disabled={!canExport || exporting || !data}
          {...(canExport ? {} : { 'aria-describedby': 'bi-export-gap' })}
        >
          <Icon name="download-simple" />
          {exporting ? 'Exporting…' : 'Export'}
        </Button>
      </div>
      {range === 'CUSTOM' && (
        <CustomRangeFields
          from={customFrom}
          to={customTo}
          onFrom={setCustomFrom}
          onTo={setCustomTo}
          errors={custom.errors}
          window={{ from: custom.from, to: custom.to }}
        />
      )}
    </>
  );

  /*
   * A custom window nobody has finished choosing.
   *
   * Deliberately BEFORE the loading and failure branches: nothing was
   * requested, so there is nothing loading and nothing has failed. The screen
   * says which window it is showing — none — rather than leaving the last
   * range's KPIs under a Custom button.
   */
  if (awaitingCustom) {
    return (
      <>
        {header('No window is being shown — pick both dates below.')}
        <Panel>
          <EmptyState icon="calendar-blank" title="Choose the custom window">
            Nothing is read until a first and a last day are both set. The figures that were here
            belonged to the previous range, so they have been taken down rather than left under a
            Custom button as though they answered it.
          </EmptyState>
        </Panel>
      </>
    );
  }
  if (loading && !data) {
    return (
      <>
        {header('Reading the period…')}
        <div className="stat-grid">
          {Array.from({ length: 5 }, (_, i) => (
            <div className="stat" key={i}>
              <div className="skeleton" style={{ height: '60px' }}>
                &nbsp;
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }
  /*
   * Nothing was read. The modal has been seen and dismissed; what is left must
   * name the failure rather than fall through to an empty report. And because
   * the catch clears `data`, everything below is drawn from a read that
   * SUCCEEDED — which is what keeps the "real zero" copy in the GGR trend panel
   * true whenever it appears.
   */
  if (!data) {
    if (!loadFailed) return null;
    return (
      <>
        {header('Nothing is being shown — the last read failed.')}
        <Panel>
          <EmptyState
            icon="warning-circle"
            title="Performance could not be read"
            action={<Button onClick={() => setAttempt((n) => n + 1)}>Try again</Button>}
          >
            Performance could not be fetched, so no KPI, no trend and no top-players list is drawn for
            this period. Nothing here is a zero — the period was never measured.
          </EmptyState>
        </Panel>
      </>
    );
  }

  const { summary, trend, priorTrend, weeklyFlows, topGames, topPlayers } = data;

  /*
   * WHICH WINDOW THE FIGURES BELOW ARE FOR — read off the payload that carried
   * them, and labelled with the range THAT read asked for rather than the one
   * the control happens to be set to now.
   *
   * The custom branch prints Manila gaming days and says so. It has to: the
   * operator picked calendar dates in Manila, and `summary.range.to` is the
   * boundary that CLOSES the window rather than a day inside it, so printing
   * either of them in the reader's own zone would name days the figures do not
   * cover.
   */
  const shownWindow =
    shownRange === 'CUSTOM' ? (
      <>
        Custom window · {gamingDayName(summary.range.from)} –{' '}
        {lastIncludedDayName(summary.range.to)} · vs {gamingDayName(summary.range.comparedFrom)} –{' '}
        {lastIncludedDayName(summary.range.comparedTo)} · Asia/Manila gaming days
      </>
    ) : (
      <>
        {gamingDate(summary.range.from)} – {gamingDate(summary.range.to)} · vs{' '}
        {gamingDate(summary.range.comparedFrom)} – {gamingDate(summary.range.comparedTo)}
      </>
    );

  return (
    <>
      {header(shownWindow)}

      {canExport ? null : (
        <div className="notice notice-quiet" id="bi-export-gap">
          <Icon name="info" />
          <div>
            <strong>Export needs the audit.export permission.</strong> Your role can read this
            view but not take a copy of it out of the platform. The figures below are the real
            daily totals and can be read here.
          </div>
        </div>
      )}

      <div className="stat-grid">
        <Stat
          label="GGR"
          value={formatPesosCompact(BigInt(summary.ggr.value))}
          delta={<Delta bp={summary.ggr.deltaBp} />}
          deltaUp={(summary.ggr.deltaBp ?? 0) > 0}
        />
        <Stat
          label="Deposits"
          value={formatPesosCompact(BigInt(summary.deposits.value))}
          delta={<Delta bp={summary.deposits.deltaBp} />}
          deltaUp={(summary.deposits.deltaBp ?? 0) > 0}
        />
        <Stat
          label="Withdrawals"
          value={formatPesosCompact(BigInt(summary.withdrawals.value))}
          delta={<Delta bp={summary.withdrawals.deltaBp} />}
        />
        <Stat
          label="Active players"
          value={formatCount(summary.activePlayers.value)}
          delta={<Delta bp={summary.activePlayers.deltaBp} />}
          deltaUp={(summary.activePlayers.deltaBp ?? 0) > 0}
        />
        <Stat
          label="ARPU"
          value={formatPesos(BigInt(summary.arpu))}
          delta={
            summary.holdBp === null ? (
              <span className="text-subtle">no turnover</span>
            ) : (
              <span>Hold {(summary.holdBp / 100).toFixed(1)}%</span>
            )
          }
        />
      </div>

      <Panel
        title="GGR trend"
        actions={
          <span className="row" style={{ gap: 'var(--space-4)', fontSize: '11.5px' }}>
            <span className="row" style={{ gap: 'var(--space-1)' }}>
              <span className="legend-swatch current" />
              This period
            </span>
            <span className="row" style={{ gap: 'var(--space-1)' }}>
              <span className="legend-swatch prior" />
              Prior period
            </span>
          </span>
        }
      >
        {trend.length > 0 ? (
          <ComparisonChart
            current={trend.map((t) => Number(BigInt(t.ggr) / 100n))}
            prior={priorTrend.map((t) => Number(BigInt(t.ggr) / 100n))}
            labels={trend.map((t) => t.day)}
          />
        ) : (
          <EmptyState icon="chart-line" title="No activity in this range">
            Nothing was staked between these dates. This is a real zero, not a missing feed.
          </EmptyState>
        )}
      </Panel>

      <div className="halves">
        <Panel title="Deposits vs withdrawals · weekly">
          {weeklyFlows.length > 0 ? (
            <WeeklyBars
              rows={weeklyFlows.map((w) => ({
                label: new Date(w.week).toLocaleDateString('en-PH', {
                  day: 'numeric',
                  month: 'short',
                }),
                deposits: Number(BigInt(w.deposits) / 100n),
                withdrawals: Number(BigInt(w.withdrawals) / 100n),
              }))}
            />
          ) : (
            <EmptyState icon="chart-bar" title="No weeks in this range">
              Pick a longer range to see weekly flows.
            </EmptyState>
          )}
        </Panel>

        <Panel
          title="Top games by GGR"
          actions={
            <Button variant="ghost" onClick={() => navigate('/games')}>
              All games
            </Button>
          }
        >
          <div className="table-scroll">
            <table className="table responsive">
              <caption className="sr-only">Top games by gross gaming revenue, with hold</caption>
              <thead>
                <tr>
                  <th scope="col">Game</th>
                  <th scope="col">Provider</th>
                  <th scope="col" className="num">
                    Hold
                  </th>
                  <th scope="col" className="num">
                    GGR
                  </th>
                </tr>
              </thead>
              <tbody>
                {topGames.map((g) => (
                  <tr key={g.gameId}>
                    <td data-label="Game">{g.name}</td>
                    <td data-label="Provider" className="text-muted">
                      {g.provider}
                    </td>
                    <td data-label="Hold" className="num">
                      {g.holdBp === null ? '—' : `${(g.holdBp / 100).toFixed(1)}%`}
                    </td>
                    <td data-label="GGR" className="num">
                      {formatPesosCompact(BigInt(g.ggr))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      <Panel
        title="Top players · net deposits"
        actions={
          <Button variant="ghost" onClick={() => navigate('/players')}>
            View all
          </Button>
        }
      >
        <div className="table-scroll">
          <table className="table responsive">
            <caption className="sr-only">
              Top players by net deposits, with deposits, withdrawals and GGR
            </caption>
            <thead>
              <tr>
                <th scope="col">Player</th>
                <th scope="col">Brand</th>
                <th scope="col">Agent</th>
                <th scope="col">Tier</th>
                <th scope="col" className="num">
                  Deposits
                </th>
                <th scope="col" className="num">
                  Withdrawals
                </th>
                <th scope="col" className="num">
                  Net
                </th>
                <th scope="col" className="num">
                  GGR
                </th>
              </tr>
            </thead>
            <tbody>
              {topPlayers.map((p) => (
                <tr
                  key={p.playerId}
                  // A clickable row is a real control. DataTable makes its own
                  // rows reachable and operable from the keyboard; these
                  // hand-rolled tables have to do the same, or the only way to
                  // open a top player is with a mouse.
                  tabIndex={0}
                  role="button"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/players/${p.playerId}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/players/${p.playerId}`);
                    }
                  }}
                >
                  <td data-label="Player">
                    {p.name}
                    <div className="text-subtle" style={{ fontSize: '11px' }}>
                      {p.ref}
                    </div>
                  </td>
                  <td data-label="Brand" className="text-muted">
                    {p.brandName}
                  </td>
                  <td data-label="Agent" className="text-muted">
                    {p.agentRef ?? '—'}
                  </td>
                  <td data-label="Tier">
                    <Tag tone={p.tier.startsWith('VIP') ? 'accent' : 'neutral'}>
                      {p.tier.replace('_', ' ')}
                    </Tag>
                  </td>
                  <td data-label="Deposits" className="num">
                    {formatPesosCompact(BigInt(p.deposits))}
                  </td>
                  <td data-label="Withdrawals" className="num">
                    {formatPesosCompact(BigInt(p.withdrawals))}
                  </td>
                  <td data-label="Net" className="num">
                    {formatPesos(BigInt(p.netDeposits))}
                  </td>
                  <td data-label="GGR" className="num">
                    {formatPesosCompact(BigInt(p.ggr))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}
